import { bopApi } from '@/lib/api/client';
import type { TransferDirection } from './funds-transactions';

const SEARCH_PATH = 'v1/users/search';
const BALANCE_ESTIMATE_PATH = 'v1/users/balance-estimate';

/** Server caps results — total === users.length (no pagination). */
export const USER_SEARCH_LIMIT = 50;

export type UserSegment = 'RETAIL' | 'INSTITUTIONAL' | 'MARKET_MAKER';
export type UserStatus = 'active' | 'blocked' | 'pending' | 'offboarded' | 'deleted';

export type SearchMatchType = 'WALLET_ADDRESS' | 'TRANSACTION_HASH';
export type SearchMatchSource = 'PAYOUT_ACCOUNT' | 'DEPOSIT_ADDRESS' | 'TRANSACTION';

/** Why a user came back for a wallet address / tx hash search. Only the source's own fields are set. */
export interface SearchUserMatch {
  matchType: SearchMatchType;
  source: SearchMatchSource;
  currency: string | null;
  /** ISO timestamp: payout account created, deposit address created, or transaction requested */
  createdAt: string | null;
  payoutAccountId: string | null;
  state: string | null;
  label: string | null;
  isActive: boolean | null;
  transactionId: string | null;
  direction: TransferDirection | null;
  amount: string | null;
  transferStatus: string | null;
}

/** The subaccount the query actually matched; the row itself is its account holder. */
export interface SearchUserSubaccount {
  /** null when bop-api could not resolve the subaccount's own PID */
  pid: string | null;
  fastId: string;
}

export interface SearchUser {
  /** UUID — always the account holder, never a subaccount */
  pid: string;
  fastId: string | null;
  isBroker: boolean;
  email: string | null;
  /** EPOCH SECONDS (not ms) */
  created: number | null;
  firstName: string | null;
  lastName: string | null;
  segment: UserSegment;
  status: UserStatus;
  /** Only present for Blockchain searches */
  matches?: SearchUserMatch[];
  /** Only present when a PID / Fast ID search matched a subaccount */
  subaccount?: SearchUserSubaccount;
}

export interface UserSearchResponse {
  users: SearchUser[];
  total: number;
}

export interface BalanceEstimate {
  estimatedEur: number;
  /** EPOCH MILLISECONDS (unlike SearchUser.created, which is seconds) */
  timestamp: number;
  sources: string[];
}

export interface BalanceEstimateResponse {
  estimate: BalanceEstimate | null;
}

// NOTE: terms.ts deliberately carries STRICTER legacy variants of these (its
// EMAIL_REGEX requires a dotted domain; its PID_REGEX constrains uuid
// version/variant nibbles) — BopTermsTableControls parity. Do not unify.
export const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const FAST_ID_REGEX = /^-?\d+$/;
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Single token long enough to be a wallet address or tx hash on any supported chain; ':' allows a "bitcoincash:" prefix.
// Mirrors bop-api searchUsers, which falls back to name search when funds does not recognise the value.
export const BLOCKCHAIN_REFERENCE_REGEX = /^[A-Za-z0-9:]{25,128}$/;

export type QueryType = 'PID' | 'Fast ID' | 'Email' | 'Blockchain' | 'Name';

/** Detection order matters: UUID → Fast ID → Email → Blockchain → Name. Blank input → null. */
export const detectType = (query: string): QueryType | null => {
  const trimmed = query.trim();
  if (!trimmed) return null;
  if (UUID_REGEX.test(trimmed)) return 'PID';
  if (FAST_ID_REGEX.test(trimmed)) return 'Fast ID';
  if (EMAIL_REGEX.test(trimmed)) return 'Email';
  if (BLOCKCHAIN_REFERENCE_REGEX.test(trimmed)) return 'Blockchain';
  return 'Name';
};

// Name searches can take up to ~20s server-side — within the client's 30s timeout,
// but callers must keep a visible loading state.
export const searchUsers = (q: string) => bopApi.get(SEARCH_PATH, { searchParams: { q: q.trim(), limit: USER_SEARCH_LIMIT } }).json<UserSearchResponse>();

/** `fastId` must match FAST_ID_REGEX — callers gate on it before fetching. */
export const getBalanceEstimate = (fastId: string) => bopApi.get(BALANCE_ESTIMATE_PATH, { searchParams: { fastId } }).json<BalanceEstimateResponse>();
